/**
 * LeadBoardView — the Kanban Lens (AD-046 §7.3). Standard|Fiverr pipeline
 * toggle. Standard columns = the live `state` spine; parked/rejected in a
 * collapsed off-ramp rail; converted terminal (L-8). Fiverr columns = the
 * `fstage` enum. DRAG is now FREE & bidirectional: any column whose key !==
 * the card's current column is a valid drop target. A FORWARD / lateral move
 * (`canTransition` true for Standard; a higher fstage index for Fiverr)
 * persists immediately, exactly as before. A BACKWARD / out-of-flow move
 * (regression) opens an in-app confirm dialog first and only persists on
 * confirm — never a silent snap-back. The global state machine in `lib/leads`
 * is untouched: regressions are written as a direct state correction by the
 * parent, not by widening `TRANSITIONS`. Source filter chips + per-card source
 * tag; per-column count + (gated) value sum. The open card/row is given a
 * clear `selected` treatment. Mobile (≤860px) → one column + a horizontal
 * stage-chip selector with "→ advance". Every card also carries a keyboard
 * "→ advance" button (desktop + mobile) so a card can move forward without drag.
 */
import React, { useMemo, useState } from 'react';
import {
  ALL_LEAD_SOURCES,
  LEAD_SOURCE_LABEL,
  LEAD_STATE_LABEL,
  canTransition,
  type Lead,
  type LeadSource,
  type LeadState,
} from '../../lib/leads';
import {
  boardForwardColumns,
  boardOffRampColumns,
  leadValue,
  sourceColour,
  sourceMap,
} from '../../lib/leadLenses';
import {
  ALL_FSTAGES,
  FSTAGE_LABEL,
  fstageOf,
  pipelineOf,
  type FStage,
} from '../../lib/leadPipeline';
import { inr } from '../../lib/trackDemo';
import MoveConfirmDialog from './MoveConfirmDialog';

type Pipe = 'standard' | 'fiverr';

export default function LeadBoardView({
  leads,
  onSelect,
  selectedId,
  onState,
  onFstage,
  onMoveBack,
  onFstageMoveBack,
  seesMoney,
  canCommit,
  isMobile,
}: {
  leads: Lead[];
  onSelect: (id: string) => void;
  /** Currently-open lead — gets the `selected` treatment. */
  selectedId?: string | null;
  /** Forward / lateral Standard move — persists immediately (as before). */
  onState: (lead: Lead, to: LeadState) => void;
  /** Forward Fiverr move — persists immediately (as before). */
  onFstage: (lead: Lead, to: FStage) => void;
  /** CONFIRMED out-of-flow Standard move (regression) — direct state correction.
   *  Optional so the dev-harness (no persistence) can mount the board. */
  onMoveBack?: (lead: Lead, to: LeadState) => void;
  /** CONFIRMED out-of-flow Fiverr move (regression) — direct fstage correction. */
  onFstageMoveBack?: (lead: Lead, to: FStage) => void;
  seesMoney: boolean;
  canCommit: boolean;
  isMobile: boolean;
}) {
  const [pipe, setPipe] = useState<Pipe>('standard');
  // SOURCE FILTER = the REAL LeadSource enum (streamline 1e, 2026-07-03).
  // The old chips offered the folded 5-source placeholder reading
  // (Website/Referral/Facebook/Google/Fiverr) — values no stored lead can
  // carry, so three of five chips always filtered to nothing. Filtering now
  // matches `lead.source` exactly; the folded reading stays DISPLAY-ONLY on
  // the card tag (locked L-3). The Fiverr PIPE toggle above is untouched.
  const [sourceFilter, setSourceFilter] = useState<LeadSource | 'all'>('all');
  const [dragId, setDragId] = useState<string | null>(null);
  // mobile single-column selection
  const [mobileCol, setMobileCol] = useState<string>('');
  // A pending out-of-flow (regression) move awaiting in-app confirmation.
  const [confirmMove, setConfirmMove] = useState<{
    lead: Lead;
    to: string;
    fiverr: boolean;
  } | null>(null);

  const inPipe = useMemo(
    () => leads.filter((l) => pipelineOf(l) === pipe),
    [leads, pipe],
  );
  const shown = useMemo(
    () =>
      sourceFilter === 'all'
        ? inPipe
        : inPipe.filter((l) => l.source === sourceFilter),
    [inPipe, sourceFilter],
  );

  const fwd = boardForwardColumns(pipe);
  const ramp = boardOffRampColumns(pipe);

  function colLeads(key: string): Lead[] {
    if (pipe === 'fiverr') return shown.filter((l) => fstageOf(l) === key);
    return shown.filter((l) => l.state === key);
  }

  /** The column key a lead currently sits in, for the active pipe. */
  function currentColKey(lead: Lead): string {
    return pipe === 'fiverr' ? fstageOf(lead) : lead.state;
  }

  // DRAG is free & bidirectional: every column whose key differs from the
  // card's current column is a valid drop target (no forward-only gate). On
  // drop we CLASSIFY: a forward / lateral move persists immediately; a
  // backward / out-of-flow move opens the confirm dialog and persists only on
  // confirm. Same-column drops are a no-op.
  function doDrop(lead: Lead, key: string) {
    if (!canCommit) return;
    if (key === currentColKey(lead)) return; // no-op: dropped on own column
    if (pipe === 'fiverr') {
      const fromIdx = ALL_FSTAGES.indexOf(fstageOf(lead));
      const toIdx = ALL_FSTAGES.indexOf(key as FStage);
      if (toIdx > fromIdx) {
        onFstage(lead, key as FStage); // forward → persist now
      } else {
        setConfirmMove({ lead, to: key, fiverr: true }); // regression → confirm
      }
      return;
    }
    if (canTransition(lead.state, key as LeadState)) {
      onState(lead, key as LeadState); // forward / lateral → persist now
    } else {
      setConfirmMove({ lead, to: key, fiverr: false }); // regression → confirm
    }
  }

  function commitConfirmedMove() {
    if (!confirmMove) return;
    const { lead, to, fiverr } = confirmMove;
    if (fiverr) onFstageMoveBack?.(lead, to as FStage);
    else onMoveBack?.(lead, to as LeadState);
    setConfirmMove(null);
  }

  const confirmToLabel = confirmMove
    ? confirmMove.fiverr
      ? FSTAGE_LABEL[confirmMove.to as FStage]
      : LEAD_STATE_LABEL[confirmMove.to as LeadState]
    : '';

  function Card({ l }: { l: Lead }) {
    const selected = l.id === selectedId;
    return (
      <div
        data-lead-id={l.id}
        draggable={canCommit}
        onDragStart={() => setDragId(l.id)}
        onDragEnd={() => setDragId(null)}
        onClick={() => onSelect(l.id)}
        onKeyDown={(e) => {
          // Keyboard parity with the mouse click (a11y): Enter/Space opens the
          // card. Guard on the card itself so the nested advance button keeps
          // its own Enter/Space behaviour.
          if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
            e.preventDefault();
            onSelect(l.id);
          }
        }}
        tabIndex={0}
        aria-label={`Open lead ${l.name}`}
        aria-current={selected ? true : undefined}
        style={{
          // Selected: 2px accent border + soft accent elevation so it's obvious
          // which card the open panel belongs to (source stripe kept on the left).
          border: selected
            ? '2px solid var(--accent, #008069)'
            : '1px solid var(--rule, #E5E7EB)',
          borderLeft: `3px solid ${sourceColour(l.source)}`,
          borderRadius: 'var(--r-sm, 9px)',
          background: 'var(--paper, #fff)',
          boxShadow: selected ? '0 8px 22px -10px rgba(0,128,105,.55)' : undefined,
          padding: '7px 9px',
          marginBottom: 7,
          cursor: canCommit ? 'grab' : 'pointer',
          fontSize: 12,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 2 }}>{l.name}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
          <span style={{ fontSize: 9.5, fontWeight: 700, color: sourceColour(l.source) }}>
            {sourceMap(l.source)}
          </span>
          <span style={{ fontSize: 10, color: 'var(--ink-mute, #6B7280)' }}>
            {seesMoney && leadValue(l) > 0 ? inr(leadValue(l)) : ''}
          </span>
        </div>
        {/* inline keyboard advance — a per-card forward hop that works without
            drag (a11y), on desktop and mobile alike. */}
        {canCommit && (
          <CardAdvance l={l} pipe={pipe} onState={onState} onFstage={onFstage} />
        )}
      </div>
    );
  }

  function Column({ keyId, label, terminal }: { keyId: string; label: string; terminal?: boolean }) {
    const items = colLeads(keyId);
    const sum = items.reduce((a, l) => a + leadValue(l), 0);
    const isDropTarget = dragId != null;
    const draggedLead = dragId ? shown.find((l) => l.id === dragId) : null;
    // Free drag: any column that isn't the card's own column accepts the drop
    // (forward persists immediately; backward routes through the confirm dialog).
    const wouldAccept = draggedLead ? currentColKey(draggedLead) !== keyId : false;
    return (
      <div
        onDragOver={(e) => {
          if (wouldAccept) e.preventDefault();
        }}
        onDrop={() => {
          if (draggedLead) doDrop(draggedLead, keyId);
          setDragId(null);
        }}
        style={{
          flex: isMobile ? '1 1 100%' : '0 0 clamp(240px, 18vw, 280px)',
          minWidth: isMobile ? undefined : 'clamp(240px, 18vw, 280px)',
          background: 'var(--inset, #F6F7F9)',
          border: '1px solid var(--rule, #E5E7EB)',
          borderRadius: 'var(--r-md, 13px)',
          padding: 8,
          outline: isDropTarget && wouldAccept ? '2px dashed var(--accent, #008069)' : 'none',
          opacity: isDropTarget && !wouldAccept ? 0.55 : 1,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.03em', color: terminal ? 'var(--good, #15803D)' : 'var(--ink-soft, #4B5563)' }}>
            {label}
          </span>
          <span style={{ fontSize: 10, color: 'var(--ink-mute, #6B7280)' }}>
            {items.length}{seesMoney && sum > 0 ? ` · ${inr(sum)}` : ''}
          </span>
        </div>
        {items.map((l) => <Card key={l.id} l={l} />)}
      </div>
    );
  }

  const mobileColumns = pipe === 'fiverr' ? ALL_FSTAGES as string[] : fwd.map((c) => c.key);
  const activeMobileCol = mobileCol || mobileColumns[0];

  return (
    <div style={{ padding: 12 }}>
      {/* pipeline + source filter */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 10 }}>
        <div style={{ display: 'inline-flex', border: '1px solid var(--rule-strong, #D1D5DB)', borderRadius: 'var(--r-pill, 999px)', overflow: 'hidden', fontWeight: 700, fontSize: 11.5 }}>
          {(['standard', 'fiverr'] as Pipe[]).map((p) => (
            <button key={p} type="button" onClick={() => setPipe(p)} style={{ padding: '5px 13px', border: 'none', background: pipe === p ? 'var(--accent, #008069)' : 'transparent', color: pipe === p ? '#fff' : 'var(--ink-soft, #4B5563)', cursor: 'pointer' }}>
              {p === 'standard' ? 'Standard' : 'Fiverr'}
            </button>
          ))}
        </div>
        {pipe === 'standard' && (
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            <FilterChip label="All" on={sourceFilter === 'all'} onClick={() => setSourceFilter('all')} />
            {ALL_LEAD_SOURCES.map((s) => (
              <FilterChip key={s} label={LEAD_SOURCE_LABEL[s]} colour={sourceColour(s)} on={sourceFilter === s} onClick={() => setSourceFilter(s)} />
            ))}
          </div>
        )}
        {!canCommit && <span style={{ fontSize: 10.5, color: 'var(--warn, #B45309)' }}>read-only (no commit rights)</span>}
      </div>

      {/* mobile single-column selector */}
      {isMobile && (
        <div style={{ display: 'flex', gap: 5, overflowX: 'auto', marginBottom: 8 }}>
          {mobileColumns.map((k) => {
            const label = pipe === 'fiverr' ? FSTAGE_LABEL[k as FStage] : LEAD_STATE_LABEL[k as LeadState];
            const on = k === activeMobileCol;
            return (
              <button key={k} type="button" onClick={() => setMobileCol(k)} style={{ flex: '0 0 auto', padding: '5px 11px', borderRadius: 'var(--r-pill, 999px)', border: '1px solid var(--rule-strong, #D1D5DB)', background: on ? 'var(--accent, #008069)' : 'var(--paper, #fff)', color: on ? '#fff' : 'var(--ink-soft, #4B5563)', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer' }}>
                {label} ({colLeads(k).length})
              </button>
            );
          })}
        </div>
      )}

      {/* board */}
      {isMobile ? (
        <Column
          keyId={activeMobileCol}
          label={pipe === 'fiverr' ? FSTAGE_LABEL[activeMobileCol as FStage] : LEAD_STATE_LABEL[activeMobileCol as LeadState]}
        />
      ) : (
        // Single horizontal scroll axis: forward columns, then (standard only,
        // when there's an off-ramp) a vertical "Lost / Parked" divider, then the
        // ramp columns — all inside ONE overflowX:auto row.
        <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 6 }}>
          {fwd.map((c) => (
            <Column key={c.key} keyId={c.key} label={c.label} terminal={c.terminal} />
          ))}
          {pipe === 'standard' && ramp.length > 0 && (
            <div
              aria-hidden="true"
              style={{
                flex: '0 0 auto',
                alignSelf: 'stretch',
                borderLeft: '1px solid var(--rule, #E5E7EB)',
                paddingLeft: 10,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  color: 'var(--warn, #B45309)',
                  writingMode: 'vertical-rl',
                  transform: 'rotate(180deg)',
                  letterSpacing: '.03em',
                  whiteSpace: 'nowrap',
                }}
              >
                Lost / Parked
              </span>
            </div>
          )}
          {pipe === 'standard' && ramp.length > 0 && ramp.map((c) => (
            <Column key={c.key} keyId={c.key} label={c.label} />
          ))}
        </div>
      )}

      {/* Out-of-flow (regression) move — confirm before persisting. */}
      {confirmMove && (
        <MoveConfirmDialog
          title="Move lead back?"
          body={`Move ${confirmMove.lead.name} to ${confirmToLabel}? This moves the lead out of its normal forward flow.`}
          confirmLabel="Move back"
          cancelLabel="Cancel"
          onConfirm={commitConfirmedMove}
          onCancel={() => setConfirmMove(null)}
          restoreFocusId={confirmMove.lead.id}
        />
      )}
    </div>
  );
}

function CardAdvance({
  l,
  pipe,
  onState,
  onFstage,
}: {
  l: Lead;
  pipe: Pipe;
  onState: (lead: Lead, to: LeadState) => void;
  onFstage: (lead: Lead, to: FStage) => void;
}) {
  if (pipe === 'fiverr') {
    const cur = fstageOf(l);
    const idx = ALL_FSTAGES.indexOf(cur);
    const next = ALL_FSTAGES[idx + 1];
    if (!next) return null;
    return (
      <button type="button" aria-label={`Advance ${l.name} to ${FSTAGE_LABEL[next]}`} onClick={(e) => { e.stopPropagation(); onFstage(l, next); }} style={advBtn}>
        → {FSTAGE_LABEL[next]}
      </button>
    );
  }
  // standard: offer the next legal forward spine hop (not converted)
  const legalNext = (['qualifying', 'qualified', 'consult_booked', 'discovery_done', 'quoted'] as LeadState[]).find(
    (s) => canTransitionSafe(l.state, s),
  );
  if (!legalNext) return null;
  return (
    <button type="button" aria-label={`Advance ${l.name} to ${LEAD_STATE_LABEL[legalNext]}`} onClick={(e) => { e.stopPropagation(); onState(l, legalNext); }} style={advBtn}>
      → {LEAD_STATE_LABEL[legalNext]}
    </button>
  );
}

function canTransitionSafe(from: LeadState, to: LeadState): boolean {
  return canTransition(from, to);
}

const advBtn: React.CSSProperties = {
  marginTop: 6,
  fontSize: 10.5,
  fontWeight: 700,
  padding: '3px 9px',
  borderRadius: 'var(--r-pill, 999px)',
  border: '1px solid var(--accent, #008069)',
  background: 'var(--accent-wash, #E9F7EF)',
  color: 'var(--accent-deep, #075E54)',
  cursor: 'pointer',
};

function FilterChip({ label, on, onClick, colour }: { label: string; on: boolean; onClick: () => void; colour?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '3px 10px',
        borderRadius: 'var(--r-pill, 999px)',
        border: `1px solid ${on ? (colour ?? 'var(--accent, #008069)') : 'var(--rule, #E5E7EB)'}`,
        background: on ? (colour ? `${colour}1a` : 'var(--accent-wash, #E9F7EF)') : 'var(--paper, #fff)',
        color: on ? (colour ?? 'var(--accent-deep, #075E54)') : 'var(--ink-soft, #4B5563)',
        fontSize: 10.5,
        fontWeight: 700,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}
